/**
 * Iter 428.p7 — Shared workbench-kind catalogue.
 *
 * Single source of truth for the flat list of purpose flags a workbench
 * can carry. Kept in a tiny module so every consumer (project Planning
 * & Reports tab, the /projects/workbench index composer, the
 * CreateReportModal on the Engineering Desk) picks from the same set
 * and the same emoji-free chip label rendering.
 *
 * "custom:*" is not in the catalogue — the FE surfaces an "Add other"
 * text input that emits `custom:<slug>` on the wire, and this module
 * exports a small formatter for those too.
 */

export const WORKBENCH_KINDS = [
  { key: "proposal",              label: "Proposal",             group: "core"    },
  { key: "planning",              label: "Planning",             group: "core"    },
  { key: "pump_assessment",       label: "Pump Assessment",      group: "reports" },
  { key: "technical_report",      label: "Technical Report",     group: "reports" },
  { key: "engineering_note",      label: "Engineering Note",     group: "reports" },
  { key: "inspection",            label: "Inspection",           group: "reports" },
  { key: "financial_breakdown",   label: "Financial Breakdown",  group: "reports" },
  { key: "general_report",        label: "General Report",       group: "reports" },
];

export const KIND_GROUPS = [
  { key: "all",      label: "All"      },
  { key: "proposal", label: "Proposal" },
  { key: "planning", label: "Planning" },
  { key: "reports",  label: "Reports"  },
];

export function isReportKind(k) {
  if (!k) return false;
  if (typeof k === "string" && k.startsWith("custom:")) return false;
  const spec = WORKBENCH_KINDS.find((x) => x.key === k);
  return !!spec && spec.group === "reports";
}

/**
 * Prettify a raw kind value for display. Handles custom:* by title-
 * casing the slug, and unknowns by echoing the raw string.
 */
export function labelForKind(k) {
  if (!k) return "Proposal";
  if (typeof k === "string" && k.startsWith("custom:")) {
    const slug = k.slice(7).replace(/_/g, " ");
    return slug.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  const spec = WORKBENCH_KINDS.find((x) => x.key === k);
  return spec ? spec.label : k;
}

/**
 * Which chip group does a kind belong to?
 * - "proposal" and "planning" map to their own groups.
 * - Every builtin `reports` kind + any `custom:*` maps to "reports".
 */
export function groupForKind(k) {
  if (!k) return "proposal";
  if (typeof k === "string" && k.startsWith("custom:")) return "reports";
  const spec = WORKBENCH_KINDS.find((x) => x.key === k);
  return spec ? spec.group : "reports";
}

/**
 * Validate + normalise a user-supplied custom kind (from the "Add
 * other" text input). Returns null if invalid. Accepts only
 * lowercase letters, digits, and underscores; caps at 40 chars.
 */
export function normaliseCustomKind(raw) {
  if (!raw) return null;
  const slug = String(raw).trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
  if (!slug) return null;
  return `custom:${slug}`;
}
